"use client";

import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import {
  isHrtStorefrontBookable,
  isOrgExperityBookable,
} from "@/app/directories/shop/lib/storefrontLogicHelper";
import { useTranslations } from "@/lib/next-intl-typed";
import { partitionHrts } from "@frontend/components/InsuranceStepper/InsuranceStepper.helper";
import type { StorefrontProductPageHrtType } from "@frontend/components/InsuranceStepper/InsuranceStepper.type";
import { EligibilityButton } from "@frontend/components/eligibility/EligibilityButton";
import { CalculatingPricesPill } from "@frontend/components/priceToggle/CalculatingPricesPill";
import { PriceToggleSymbol } from "@frontend/components/priceToggle/priceToggleSymbol";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Label } from "@frontend/shared/components/ui/label";
import { PriceSwitch } from "@frontend/shared/components/ui/switch";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { ArrowLeft, X } from "lucide-react";
import Link from "next/link";
import { calculatePrice } from "./HrtSelector";
import { InitialFollowUpSelector } from "./InitialFollowUpSelector";
import { useProduct } from "./ProductContext";
import { VerticalHrtOptionsGrid } from "./VerticalHrtOptionsGrid";

interface VerticalHrtSelectorProps {
  clientSlug: string;
  orgId: number;
  product: {
    name: string;
    displayMetadata?: {
      imageUrl: string;
      preDiscount: string;
    };
    productToHrt: Array<{
      hrtId: number;
      product_component_type: string;
      hrt: StorefrontProductPageHrtType;
    }>;
  };
  onOpenChange: (open: boolean) => void;
  kioskMode: boolean;
}

export function VerticalHrtSelector({
  clientSlug,
  orgId,
  product,
  onOpenChange,
  kioskMode,
}: VerticalHrtSelectorProps) {
  const t = useTranslations("Shop");
  const {
    selectedHrtId,
    setSelectedHrtId,
    defaultOptions,
    initialRequiredOptions,
    selectedInitialRequiredHrtId,
    setSelectedInitialRequiredHrtId,
  } = useProduct();
  const setPricingMap = useBoundStore.use.setPricingMap();
  const storefrontPaymentMode = useBoundStore.use.storefrontPaymentMode();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const globalPricingMap = useBoundStore.use.pricingMapEntries();
  const { send, pricingMap: kioskPricingMap } = useKioskEvent();

  // Use kiosk pricing map in kiosk mode, otherwise use global store pricing map
  const pricingMap = kioskMode ? kioskPricingMap : globalPricingMap;

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";
  const secondaryColor = organizationAssets?.secondaryColor ?? "#000000";

  const handlePriceModeChange = (checked: boolean) => {
    const newPricingMap = { ...pricingMap };
    for (const option of defaultOptions) {
      const hrtId = option.hrt.hrtId;
      const isInsurable = option.hrt.hrtPaymentType.some(
        (paymentType) => paymentType.paymentType === "INSURED",
      );
      if (newPricingMap[hrtId]) {
        newPricingMap[hrtId] = {
          ...newPricingMap[hrtId],
          mode: checked && isInsurable ? "INSURED" : "CASH",
        };
      }
    }
    setPricingMap(newPricingMap);
  };

  const { hrtIds, insurableHrtIds } = partitionHrts(
    defaultOptions.flatMap((option) => option.hrt),
  );

  const isInsuredMode = defaultOptions.some(
    (option) => pricingMap[option.hrt.hrtId]?.mode === "INSURED",
  );

  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  // Check if product has an INITIAL_REQUIRED option
  const hasInitialRequired = initialRequiredOptions.some(
    (opt) => opt.product_component_type === "INITIAL_REQUIRED",
  );

  const selectedHrt = defaultOptions.find(
    (option) => option.hrt.hrtId === selectedHrtId,
  );

  const selectedInitialRequiredHrt = initialRequiredOptions.find(
    (option) => option.hrt.hrtId === selectedInitialRequiredHrtId,
  );

  const isBookable =
    isHrtStorefrontBookable(selectedHrt?.hrt, configFlags) ||
    isHrtStorefrontBookable(selectedInitialRequiredHrt?.hrt, configFlags);

  const isExperityBookable = isOrgExperityBookable(orgId, configFlags);
  const hasSelection = selectedHrtId !== undefined;

  return (
    <div className="flex flex-col min-h-fit gap-6 w-full">
      {hasInitialRequired && (
        <div className="flex flex-col min-h-fit gap-6 w-full">
          <div className="flex justify-between items-center mb-[-12px]">
            <p className="font-light font-mono text-sm text-[#767676]">
              VISIT TYPE
            </p>
          </div>
          <InitialFollowUpSelector
            initialOptions={initialRequiredOptions}
            selectedHrtId={selectedInitialRequiredHrtId}
            onHrtSelect={setSelectedInitialRequiredHrtId}
            pricingMap={pricingMap}
            calculatePrice={calculatePrice}
          />
        </div>
      )}

      {/* {!pricesLoaded && insurableHrtIds.length > 0 && (
        <InsuranceStepperContainer
          arrangementMode={"horiz"}
          tagAlignment={"center"}
          isMini={false}
          isPdp={true}
          orgId={orgId}
          clientSlug={clientSlug}
          hrtsInProduct={product.productToHrt.map((hrt) => hrt.hrt)}
          isTucked={false}
          setIsTucked={() => {}}
          isFetchingPrice={isFetchingPrice}
          text={""}
          setIsSubmitting={() => {}}
          isSubmitting={false}
        />
      )} */}
      <div className="flex justify-end w-full">
        <button
          onClick={() => onOpenChange(false)}
          className="w-fit text-black justify-center items-center flex text-sm font-normal"
          type="button"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {isFetchingPrice ? (
        <CalculatingPricesPill />
      ) : (
        <div className="flex flex-col min-h-fit gap-6 w-full">
          <div className="flex justify-between items-center mb-[-8px]">
            <p className="font-bold text-lg">{t("ChooseYourTreatment")}</p>
            {((storefrontPaymentMode === "INSURED" && pricesLoaded) ||
              isPendingPricesMode) &&
              insurableHrtIds.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Label className="text-sm font-medium">
                    {isInsuredMode ? t("InsuredPrice") : t("SelfPay")}
                  </Label>

                  <PriceSwitch
                    className=""
                    checked={isInsuredMode}
                    onCheckedChange={handlePriceModeChange}
                    disabled={insurableHrtIds.length === 0}
                  >
                    <PriceToggleSymbol checked={isInsuredMode} />
                  </PriceSwitch>
                </div>
              )}
          </div>
          <VerticalHrtOptionsGrid
            options={defaultOptions}
            selectedHrtId={selectedHrtId}
            onHrtSelect={setSelectedHrtId}
            pricingMap={pricingMap}
            calculatePrice={calculatePrice}
            isInitialRequired={false}
            noInsuranceProcedures={!!configFlags?.no_insured_procedures}
            secondaryColor={secondaryColor}
          />
        </div>
      )}

      {isInsuredMode && (
        <div className="flex justify-between w-full mb-4">
          {/* TODO: Add pricing explainability when in insured mode */}
        </div>
      )}

      <EligibilityButton
        link={
          hasSelection
            ? `/${clientSlug}/schedule?hrtId=${
                selectedInitialRequiredHrtId
                  ? `${selectedInitialRequiredHrtId},`
                  : ""
              }${selectedHrtId}`
            : "#"
        }
        orgId={orgId}
        classNames="w-full max-w-[600px]"
      >
        <div className="flex justify-between w-full">
          <button
            onClick={() => onOpenChange(false)}
            className="w-fit rounded-full py-4 px-8 bg-[white] text-black justify-center items-center flex text-sm font-normal border"
            type="button"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {kioskMode ? (
            <button
              type="button"
              className="w-fit rounded-full py-4 px-8 text-white justify-center items-center flex text-md font-normal disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
              disabled={!hasSelection}
              onClick={() => {
                if (!selectedHrtId) return;
                const hrtIds = selectedInitialRequiredHrtId
                  ? [selectedInitialRequiredHrtId, selectedHrtId]
                  : [selectedHrtId];
                send({
                  type: "SUBMIT",
                  data: { hrtIds },
                });
                onOpenChange(false);
              }}
            >
              {t("GoToCheckout")}
            </button>
          ) : isBookable ? (
            <Link
              className="w-fit rounded-full py-4 px-8 text-white justify-center items-center flex text-md font-normal aria-disabled:opacity-50 aria-disabled:pointer-events-none"
              style={{ backgroundColor: primaryColor }}
              aria-disabled={!hasSelection}
              // Order matters here, we want to make sure the initial required hrt is first
              href={
                hasSelection
                  ? `/${clientSlug}/schedule?hrtId=${
                      selectedInitialRequiredHrtId
                        ? `${selectedInitialRequiredHrtId},`
                        : ""
                    }${selectedHrtId}`
                  : "#"
              }
            >
              {t("GoToCheckout")}
            </Link>
          ) : isExperityBookable ? (
            <Link
              className="w-fit rounded-full py-4 px-8 text-white justify-center items-center flex text-md font-normal aria-disabled:opacity-50 aria-disabled:pointer-events-none"
              style={{ backgroundColor: primaryColor }}
              aria-disabled={!hasSelection}
              // Order matters here, we want to make sure the initial required hrt is first
              href={
                hasSelection
                  ? `/${clientSlug}/schedule?hrtId=${
                      selectedInitialRequiredHrtId
                        ? `${selectedInitialRequiredHrtId},`
                        : ""
                    }${selectedHrtId}`
                  : "#"
              }
            >
              {t("GoToCheckout")}
            </Link>
          ) : (
            <Link
              className="w-fit rounded-full py-4 px-8 text-white justify-center items-center flex text-md font-normal aria-disabled:opacity-50 aria-disabled:pointer-events-none"
              style={{ backgroundColor: primaryColor }}
              aria-disabled={!hasSelection}
              href={
                hasSelection
                  ? `/${clientSlug}/review?hrtId=${
                      selectedInitialRequiredHrtId
                        ? `${selectedInitialRequiredHrtId},`
                        : ""
                    }${selectedHrtId}`
                  : "#"
              }
            >
              {t("GoToCheckout")}
            </Link>
          )}
        </div>
      </EligibilityButton>
    </div>
  );
}
